import React, { useState, useRef } from 'react';
import { Button, message } from 'antd';
import {
    CloudUploadOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    CheckCircleFilled,
    CloseCircleOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import api from '../api/axios';

/* ── helpers ──────────────────────────────────────────────────── */
const ACCEPTED_TYPES = {
    'application/pdf': { ext: 'PDF', icon: <FilePdfOutlined />, color: '#EF4444' },
    'application/msword': { ext: 'DOC', icon: <FileOutlined />, color: '#3B82F6' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'DOCX', icon: <FileOutlined />, color: '#3B82F6' },
    'application/vnd.ms-excel': { ext: 'XLS', icon: <FileExcelOutlined />, color: '#10B981' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { ext: 'XLSX', icon: <FileExcelOutlined />, color: '#10B981' },
    'image/jpeg': { ext: 'JPG', icon: <FileImageOutlined />, color: '#8B5CF6' },
    'image/png': { ext: 'PNG', icon: <FileImageOutlined />, color: '#8B5CF6' },
    'text/plain': { ext: 'TXT', icon: <FileOutlined />, color: '#64748B' },
};

const ACCEPT_ATTR = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt';
const MAX_SIZE_MB = 2;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/* ── component ────────────────────────────────────────────────── */
const BiodataUpload = () => {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const inputRef = useRef();

    const validateFile = (f) => {
        if (!ACCEPTED_TYPES[f.type]) {
            message.error('Unsupported file type. Please upload a PDF, Word, Excel, or image file.');
            return false;
        }
        if (f.size > MAX_SIZE_BYTES) {
            message.error(`File is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
            return false;
        }
        return true;
    };

    const handleFile = (f) => {
        if (f && validateFile(f)) {
            setFile(f);
            setStatus(null);
            setErrorMsg('');
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) handleFile(dropped);
    };

    const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
    const onDragLeave = () => setDragging(false);

    const onInputChange = (e) => {
        const selected = e.target.files[0];
        if (selected) handleFile(selected);
        e.target.value = '';
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setStatus(null);

        const formData = new FormData();
        formData.append('biodata', file);

        try {
            await api.post('/biodata/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setStatus('success');
            setFile(null);
        } catch (err) {
            const msg = err.response?.data?.message;
            setErrorMsg(typeof msg === 'object' ? Object.values(msg).flat().join(' ') : (msg || 'Upload failed. Please try again.'));
            setStatus('error');
        } finally {
            setUploading(false);
        }
    };

    const fileInfo = file ? ACCEPTED_TYPES[file.type] : null;

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#fdf2f8 0%,#f8f0fb 50%,#f0f9ff 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', fontFamily: 'Calibri, sans-serif' }}>

            {/* ── Brand header ── */}
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg,#AD1457,#D81B60)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'white', fontWeight: 800, fontSize: '1.1rem' }}>O</span>
                    </div>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e' }}>
                        Oottan
                    </span>
                </div>
                <h1 style={{ fontSize: '1.7rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px' }}>
                    Quick Registration
                </h1>
                <p style={{ color: '#64748B', fontSize: '1rem', margin: 0, maxWidth: '440px' }}>
                    Skip the form — just upload your biodata and we'll create your profile for you.
                </p>
            </div>

            {/* ── Upload card ── */}
            <div style={{ background: 'white', borderRadius: '20px', padding: '36px', maxWidth: '520px', width: '100%', boxShadow: '0 4px 32px rgba(173,20,87,0.08)', border: '1px solid rgba(173,20,87,0.08)' }}>

                {status === 'success' ? (
                    /* ── Success state ── */
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <CheckCircleFilled style={{ fontSize: '56px', color: '#10B981', marginBottom: '16px', display: 'block' }} />
                        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 10px' }}>Biodata Received!</h2>
                        <p style={{ color: '#64748B', fontSize: '0.95rem', margin: '0 0 24px' }}>
                            Our team will process your biodata and reach out to you soon.
                        </p>
                        <Button
                            onClick={() => setStatus(null)}
                            style={{ borderColor: '#AD1457', color: '#AD1457', borderRadius: '8px', fontFamily: 'Calibri, sans-serif', fontWeight: 600 }}
                        >
                            Upload Another
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* ── Drop zone ── */}
                        <div
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onClick={() => !file && inputRef.current.click()}
                            style={{
                                border: `2px dashed ${dragging ? '#AD1457' : 'rgba(173,20,87,0.25)'}`,
                                borderRadius: '14px',
                                padding: '40px 24px',
                                textAlign: 'center',
                                cursor: file ? 'default' : 'pointer',
                                background: dragging ? 'rgba(173,20,87,0.04)' : 'rgba(173,20,87,0.015)',
                                transition: 'all 0.2s ease',
                                marginBottom: '20px',
                            }}
                        >
                            <input ref={inputRef} type="file" accept={ACCEPT_ATTR} onChange={onInputChange} style={{ display: 'none' }} />

                            {file ? (
                                /* ── File selected preview ── */
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left', justifyContent: 'center' }}>
                                    <div style={{ fontSize: '2.2rem', color: fileInfo?.color, flexShrink: 0 }}>
                                        {fileInfo?.icon}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {file.name}
                                        </div>
                                        <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginTop: '2px' }}>
                                            {fileInfo?.ext} &middot; {formatFileSize(file.size)}
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setFile(null); setStatus(null); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px', flexShrink: 0 }}
                                    >
                                        <DeleteOutlined style={{ fontSize: '16px' }} />
                                    </button>
                                </div>
                            ) : (
                                /* ── Empty state ── */
                                <>
                                    <CloudUploadOutlined style={{ fontSize: '48px', color: '#AD1457', marginBottom: '14px', display: 'block', opacity: dragging ? 1 : 0.7 }} />
                                    <p style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', margin: '0 0 6px' }}>
                                        {dragging ? 'Drop it here!' : 'Drag & drop your biodata here'}
                                    </p>
                                    <p style={{ color: '#94A3B8', fontSize: '0.85rem', margin: 0 }}>
                                        or <span style={{ color: '#AD1457', fontWeight: 600 }}>click to browse</span>
                                    </p>
                                </>
                            )}
                        </div>

                        {/* ── Supported formats ── */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginBottom: '20px' }}>
                            {[
                                { label: 'PDF', color: '#EF4444' },
                                { label: 'DOC / DOCX', color: '#3B82F6' },
                                { label: 'XLS / XLSX', color: '#10B981' },
                                { label: 'JPG / PNG', color: '#8B5CF6' },
                                { label: 'TXT', color: '#64748B' },
                            ].map(({ label, color }) => (
                                <span key={label} style={{ background: `${color}14`, color, borderRadius: '6px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                                    {label}
                                </span>
                            ))}
                            <span style={{ color: '#94A3B8', fontSize: '0.75rem', alignSelf: 'center' }}>· Max {MAX_SIZE_MB} MB</span>
                        </div>

                        {/* ── Mandatory fields notice ── */}
                        <div style={{ display: 'flex', gap: '10px', background: 'rgba(173,20,87,0.04)', border: '1px solid rgba(173,20,87,0.15)', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
                            <span style={{ color: '#AD1457', fontSize: '1rem', flexShrink: 0, marginTop: '1px' }}>ℹ</span>
                            <span style={{ color: '#64748B', fontSize: '0.82rem', lineHeight: '1.5' }}>
                                Your biodata must include your <strong style={{ color: '#1a1a2e' }}>name</strong>, <strong style={{ color: '#1a1a2e' }}>gender</strong>, <strong style={{ color: '#1a1a2e' }}>mobile number</strong>, and <strong style={{ color: '#1a1a2e' }}>email address</strong> for us to create your profile.
                            </span>
                        </div>

                        {/* ── Error message ── */}
                        {status === 'error' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
                                <CloseCircleOutlined style={{ color: '#EF4444', flexShrink: 0 }} />
                                <span style={{ color: '#EF4444', fontSize: '0.85rem' }}>{errorMsg}</span>
                            </div>
                        )}

                        {/* ── Upload button ── */}
                        <Button
                            type="primary"
                            block
                            size="large"
                            loading={uploading}
                            disabled={!file}
                            onClick={handleUpload}
                            style={{
                                background: file ? 'linear-gradient(135deg,#AD1457,#D81B60)' : undefined,
                                border: 'none',
                                borderRadius: '10px',
                                fontFamily: 'Calibri, sans-serif',
                                fontWeight: 700,
                                fontSize: '1rem',
                                height: '46px',
                            }}
                        >
                            {uploading ? 'Uploading…' : 'Upload Biodata'}
                        </Button>

                        <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '0.78rem', margin: '14px 0 0' }}>
                            Already have an account?{' '}
                            <a href="/login" style={{ color: '#AD1457', fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
                        </p>
                    </>
                )}
            </div>

            {/* ── Footer note ── */}
            <p style={{ color: '#94A3B8', fontSize: '0.78rem', marginTop: '24px', textAlign: 'center', maxWidth: '420px' }}>
                Your biodata is stored securely and used only for creating your matrimony profile. We respect your privacy.
            </p>
        </div>
    );
};

export default BiodataUpload;
